/**
 * אייקונים (6ו, A-157): SVG פנימי בקובץ אחד — אין ספריית אייקונים ואין בקשת רשת.
 *
 * 20px, `currentColor`, `aria-hidden` — האייקון הוא קישוט לצד מילה, לעולם לא לבדו
 * (RQ-J09). אייקוני כיוון (״הבא״, חזרה, chevron) מצוירים לכיוון LTR ומתהפכים ב־RTL
 * דרך המחלקה `icon-dir` (styles.css) — האפליקציה בעברית, ולכן ״הבא״ מצביע שמאלה.
 */

import type { ReactElement } from 'react';

export type IconName =
  | 'camera'
  | 'plan'
  | 'finding'
  | 'next'
  | 'back'
  | 'summary'
  | 'share'
  | 'save'
  | 'plus'
  | 'warning'
  | 'search'
  | 'upload'
  | 'zoom-in'
  | 'zoom-out'
  | 'fit'
  | 'refresh'
  | 'check'
  | 'close'
  | 'chevron'
  | 'list'
  | 'folder'
  | 'user'
  | 'rotate';

/** הקווים של כל אייקון, ברשת 24×24 (stroke 2, קצוות מעוגלים). */
const PATHS: Record<IconName, string> = {
  camera:
    'M4 8h3l2-3h6l2 3h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Zm8 10a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z',
  plan: 'M4 5h16v14H4Zm0 5h16M9 5v14M14 10v9',
  finding: 'M12 3l9 16H3Zm0 6v4m0 3v.5',
  next: 'M5 12h14m-6-6 6 6-6 6',
  back: 'M19 12H5m6 6-6-6 6-6',
  summary: 'M6 4h12v16H6Zm3 5h6M9 13h6M9 17h4',
  share: 'M12 4v12m-5-7 5-5 5 5M5 14v5h14v-5',
  save: 'M12 4v11m-5-4 5 5 5-5M5 19h14',
  plus: 'M12 5v14M5 12h14',
  warning: 'M12 3l10 18H2Zm0 7v4m0 3v.5',
  search: 'M10.5 4a6.5 6.5 0 1 1 0 13 6.5 6.5 0 0 1 0-13Zm5 11.5L20 20',
  upload: 'M12 16V5m-5 4 5-5 5 5M4 19h16',
  'zoom-in': 'M10.5 4a6.5 6.5 0 1 1 0 13 6.5 6.5 0 0 1 0-13Zm5 11.5L20 20M10.5 8v5m-2.5-2.5h5',
  'zoom-out': 'M10.5 4a6.5 6.5 0 1 1 0 13 6.5 6.5 0 0 1 0-13Zm5 11.5L20 20M8 10.5h5',
  fit: 'M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5M8 12h8',
  refresh: 'M20 12a8 8 0 1 1-2.3-5.7M20 4v5h-5',
  check: 'M5 12l5 5L20 7',
  close: 'M6 6l12 12M18 6 6 18',
  // 6ו-ב (D31, חלק 2 ס׳ 7): chevron לכפתור החזרה בפס הניווט (כיווני — ב־RTL מצביע ימינה);
  // list/folder/user ללשוניות הסרגל התחתון (חלק 3); rotate לצופה (ח׳-2).
  chevron: 'M15 6l-6 6 6 6',
  list: 'M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01',
  folder: 'M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
  user: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM4 20c1.5-3.5 4.5-5 8-5s6.5 1.5 8 5',
  rotate: 'M20 12a8 8 0 1 1-3-6.2M20 4v5h-5',
};

/** אייקונים שכיוונם תלוי בכיוון הכתיבה. */
const DIRECTIONAL: ReadonlySet<IconName> = new Set(['next', 'back', 'chevron']);

export function Icon({ name, className }: { name: IconName; className?: string }): ReactElement {
  const classes = ['icon', DIRECTIONAL.has(name) ? 'icon-dir' : '', className ?? '']
    .filter((c) => c !== '')
    .join(' ');
  return (
    <svg
      className={classes}
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={PATHS[name]} />
    </svg>
  );
}
